// src/config/picklistConfig.ts

interface PicklistFieldDataMap {
  [objectName: string]: {
    [fieldName: string]: string[];
  };
}

export const CAREGIVER_COUNTY_OPTIONS = [
  "Miami-Dade",
  "Broward",
  "Palm Beach",
  "St. Lucie",
] as const;

const picklistFieldDataMap: PicklistFieldDataMap = {
  caregiver: {
    portalStatus: [
      "Demographics",
      "Human Resources",
      "References / Emergency Contact",
      "Availability / Preferences",
      "Medical",
      "Signature",
      "Submitted",
    ],
    portalStatuses: [
      "Datos demográficos",
      "Recursos Humanos",
      "Referencias / Contacto de Emergencia",
      "Disponibilidad / Preferencias",
      "Médico",
      "Firma",
      "Enviado",
    ],

    federalFilingStatus: [
      "Single",
      "Married filing jointly",
      "Head of Household",
    ],
    federalFilingStatuses: [
      "Soltero",
      "Casado declarando conjuntamente",
      "Cabeza de familia",
    ],

    tbSymptoms: [
      "Weakness or Fatigue",
      "Current/Planned Immunosuppression",
      "Lack of Appetite/Weight Loss",
      "Low-grade fever",
      "Night Sweats",
      "Flulike symptoms",
      "Chest Pains",
      "Shortness of Breath",
      "Persistent Cough",
      "Blood tinged/Dark Sputum",
      "Temp/Perm Residence in a country with high TB rate",
      "None of the above",
    ],
    tbSymptomses: [
      "Debilidad o fatiga",
      "Inmunosupresión actual o planificada",
      "Falta de apetito / Pérdida de peso",
      "Fiebre leve",
      "Sudor nocturno",
      "Síntomas similares a la gripe",
      "Dolor en el pecho",
      "Falta de aire",
      "Tos persistente",
      "Esputo con sangre u oscuro",
      "Residencia temporal o permanente en un país con alta tasa de tuberculosis",
      "Ninguna de las anteriores",
    ],

    exposedToTb: ["Yes", "No"],
    exposedToTbes: ["Sí", "No"],

    fluVaccineDecision: ["I agree", "I disagree"],
    fluVaccineDecisiones: ["Estoy de acuerdo", "No estoy de acuerdo"],

    hepBVaccinationDecision: [
      "I accept",
      "I decline the Hepatitis B vaccine and hold harmless the agency",
      "I decline - I am already immune",
    ],
    hepBVaccinationDecisiones: [
      "Acepto",
      "Rechazo la vacuna contra la hepatitis B y eximo de responsabilidad a la agencia",
      "Rechazo - Ya soy inmune",
    ],

    sysHepBDecision: [
      "I accept",
      "I decline the Hepatitis B vaccine and hold harmless the agency",
      "I decline - I am already immune",
    ],
    sysHepBDecisiones: [
      "Acepto",
      "Rechazo la vacuna contra la hepatitis B y eximo de responsabilidad a la agencia",
      "Rechazo - Ya soy inmune",
    ],

    hepBImmuneStatus: ["Vaccinated", "Immune"],
    hepBImmuneStatuses: ["Vacunado", "Inmune"],

    fluDeclinationReason: [
      "An allergy",
      "A compromised immune system",
      "Previous adverse reaction",
      "Medical illness or contraindications",
      "Spiritual and/or religious beliefs",
      "Without providing reason",
      "Other",
    ],
    fluDeclinationReasones: [
      "Una alergia",
      "Un sistema inmunitario comprometido",
      "Reacción adversa previa",
      "Enfermedad o contraindicaciones médicas",
      "Creencias espirituales y/o religiosas",
      "Sin proporcionar razón",
      "Otro",
    ],

    hasReceivedFluVaccineThisSeason: ["Yes", "No"],
    hasReceivedFluVaccineThisSeasons: ["Sí", "No"],

    emergencyContactRelationship: [
      "Aunt",
      "Boyfriend",
      "Brother",
      "Brother-in-law",
      "Care Manager",
      "Coordinator",
      "Cousin",
      "Daughter",
      "Daughter-in-law",
      "Director",
      "Enrollment",
      "Executive",
      "Father",
      "Father-in-law",
      "Friend",
      "Girlfriend",
      "Granddaughter",
      "Granddaughter-in-law",
      "Grandfather",
      "Grandmother",
      "Grandson",
      "Grandson-in-law",
      "Great-granddaughter-in-law",
      "Great-grandson-in-law",
      "Intake",
      "Liaison",
      "Manager",
      "Medical Assistant",
      "Mother",
      "Mother-in-law",
      "Neighbor",
      "Nephew",
      "Niece",
      "Nurse",
      "Partner",
      "Receptionist",
      "Rep",
      "Secretary",
      "Sister",
      "Sister-in-law",
      "Social Worker",
      "Son",
      "Son-in-law",
      "Spouse",
      "Translator",
      "Uncle",
    ],
    emergencyContactRelationshipes: [
      "Tía",
      "Novio",
      "Hermano",
      "Cuñado",
      "Gerente de atención",
      "Coordinador",
      "Primo",
      "Hija",
      "Nuera",
      "Director",
      "Inscripción",
      "Ejecutivo",
      "Padre",
      "Suegro",
      "Amigo",
      "Novia",
      "Nieta",
      "Nieta política",
      "Abuelo",
      "Abuela",
      "Nieto",
      "Nieto político",
      "Bisnieta política",
      "Bisnieto político",
      "Admisión",
      "Enlace",
      "Gerente",
      "Asistente médico",
      "Madre",
      "Suegra",
      "Vecino",
      "Sobrino",
      "Sobrina",
      "Enfermero",
      "Pareja",
      "Recepcionista",
      "Representante",
      "Secretario",
      "Hermana",
      "Cuñada",
      "Trabajador social",
      "Hijo",
      "Yerno",
      "Cónyuge",
      "Traductor",
      "Tío",
    ],

    gender: ["Female", "Male"],
    genderes: ["Mujer", "Hombre"],

    countyName: [...CAREGIVER_COUNTY_OPTIONS],
    countyNamees: [...CAREGIVER_COUNTY_OPTIONS],

    // Used by the availability update page (/availability); values match Nexus picklist labels
    availableDays: [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ],
    availableDayses: [
      "Domingo",
      "Lunes",
      "Martes",
      "Miércoles",
      "Jueves",
      "Viernes",
      "Sábado",
    ],

    availabilityPreferences: ["Day Shift", "Evening Shift", "Overnights"],
    availabilityPreferenceses: [
      "Turno de día",
      "Turno de tarde",
      "Turnos de noche",
    ],

    areasOfWorkBroward: [
      "Bay Colony",
      "Central Beach",
      "Coconut Creek",
      "Cooper City",
      "Coral Ridge",
      "Coral Springs",
      "Dania Beach",
      "Davie",
      "Deerfield Beach",
      "Edgewood",
      "Flagler Village",
      "Fort Lauderdale",
      "Fort Lauderdale Beach",
      "Golden Isles",
      "Hallandale Beach",
      "Harbor Beach",
      "Hillsboro Beach",
      "Hollywood",
      "Imperial Point",
      "Las Olas",
      "Lauderdale-by-the-Sea",
      "Lauderdale Lakes",
      "Lauderhill",
      "Lazy Lake",
      "Lighthouse Point",
      "Margate",
      "Miramar",
      "North Lauderdale",
      "Oakland Park",
      "Parkland",
      "Pembroke Park",
      "Pembroke Pines",
      "Plantation",
      "Pompano Beach",
      "Rio Vista",
      "Sailboat Bend",
      "Sea Ranch Lakes",
      "Southwest Ranches",
      "Sunrise",
      "Tamarac",
      "Tarpon River",
      "Victoria Park",
      "Weston",
      "West Park",
      "Wilton Manors",
    ],

    areasOfWorkMiamiDade: [
      "Aventura",
      "Bal Harbour",
      "Bay Harbor Island",
      "Brickel",
      "Coconut Grove",
      "Coral Gables",
      "Coral Way",
      "Cuttler Bay",
      "Doral",
      "Downtown Miami",
      "Fisher Island",
      "Flagler",
      "Florida City",
      "Golden Beach",
      "Hialeah",
      "Hialeah Gardens",
      "Homestead",
      "Indian Creek",
      "Kendall",
      "Key Biscayne",
      "Little Havana",
      "Medley",
      "Miami Beach",
      "Miami Gardens",
      "Miami Lakes",
      "Miami Shores",
      "Miami Springs",
      "Mid Town",
      "Miller",
      "North Miami",
      "North Miami Beach",
      "Opa Locka",
      "Palmetto Bay",
      "Pinecrest",
      "South Beach",
      "South Miami Heights",
      "Sunny Isles",
      "Tamiami",
      "The Crossings",
      "The Hammocks",
      "Westchester",
      "Wynwood",
    ],

    areasOfWorkPalmBeach: [
      "Atlantis",
      "Belle Glade",
      "Boca Raton",
      "Boynton Beach",
      "Briny Breezes",
      "Cloud Lake",
      "Delray Beach",
      "Glen Ridge",
      "Golf",
      "Greenacres",
      "Gulf Stream",
      "Haverhill",
      "Highland Beach",
      "Hypoluxo",
      "Juno Beach",
      "Jupiter",
      "Jupiter Inlet Colony",
      "Lake Clarke Shores",
      "Lake Park",
      "Lake Worth Beach",
      "Lantana",
      "Loxahatchee Groves",
      "Manalapan",
      "Mangonia Park",
      "North Palm Beach",
      "Ocean Ridge",
      "Pahokee",
      "Palm Beach",
      "Palm Beach Gardens",
      "Palm Beach Shores",
      "Palm Springs",
      "Riviera Beach",
      "Royal Palm Beach",
      "South Bay",
      "South Palm Beach",
      "Tequesta",
      "Wellington",
      "West Palm Beach",
      "Westlake",
    ],

    additionalLanguages: [
      "English",
      "Spanish",
      "Albanian",
      "Arabic",
      "Bangla",
      "Bengali",
      "Bosnian",
      "Chinese",
      "Creole",
      "Farsi",
      "Filipino",
      "French",
      "Fula/Fulani",
      "Greek",
      "German",
      "Hebrew",
      "Hindi",
      "Igbo",
      "Italian",
      "Nepali",
      "Polish",
      "Portuguese",
      "Russian",
      "Sign Language - American",
      "Sinhala",
      "Tagalog",
      "Twi",
      "Urdu",
      "Yoruba",
    ],
    additionalLanguageses: [
      "Inglés",
      "Español",
      "Albanés",
      "Árabe",
      "Bangla",
      "Bengalí",
      "Bosnio",
      "Chino",
      "Criollo",
      "Farsi (Persa)",
      "Filipino",
      "Francés",
      "Fula/Fulani",
      "Griego",
      "Alemán",
      "Hebreo",
      "Hindi",
      "Igbo",
      "Italiano",
      "Nepalí",
      "Polaco",
      "Portugués",
      "Ruso",
      "Lenguaje de señas estadounidense",
      "Cingalés",
      "Tagalo",
      "Twi",
      "Urdu",
      "Yoruba",
    ],

    primaryLanguage: [
      "English",
      "Spanish",
      "Albanian",
      "Arabic",
      "Bangla",
      "Bengali",
      "Bosnian",
      "Chinese",
      "Creole",
      "Farsi",
      "Filipino",
      "French",
      "Fula/Fulani",
      "Greek",
      "German",
      "Hebrew",
      "Hindi",
      "Igbo",
      "Italian",
      "Nepali",
      "Polish",
      "Portuguese",
      "Russian",
      "Sign Language - American",
      "Sinhala",
      "Tagalog",
      "Twi",
      "Urdu",
      "Yoruba",
    ],
    primaryLanguagees: [
      "Inglés",
      "Español",
      "Albanés",
      "Árabe",
      "Bangla",
      "Bengalí",
      "Bosnio",
      "Chino",
      "Criollo",
      "Farsi (Persa)",
      "Filipino",
      "Francés",
      "Fula/Fulani",
      "Griego",
      "Alemán",
      "Hebreo",
      "Hindi",
      "Igbo",
      "Italiano",
      "Nepalí",
      "Polaco",
      "Portugués",
      "Ruso",
      "Lenguaje de señas estadounidense",
      "Cingalés",
      "Tagalo",
      "Twi",
      "Urdu",
      "Yoruba",
    ],

    citizenStatus: [
      "A citizen of the United States",
      "A noncitizen national of the United States",
      "A lawful permanent resident",
      "An alien authorized to work",
    ],
    citizenStatuses: [
      "Ciudadano Americano",
      "Un Nacional no ciudadano de los Estados Unidos",
      "Residente Permanente",
      "Un extranjero autorizado a trabajar",
    ],

    noncitizenDocumentType: [
      "USCIS Number",
      "Form I-94 Admission Number",
      "Foreign Passport",
    ],
    noncitizenDocumentTypees: [
      "Número USCIS",
      "Número de admisión del formulario I-94",
      "Pasaporte extranjero",
    ],

    homecareExperienceYears: [
      "0",
      "1",
      "2",
      "3",
      "4",
      "5",
      "6",
      "7",
      "8",
      "9",
      "10+",
    ],
    homecareExperienceYearses: [
      "0",
      "1",
      "2",
      "3",
      "4",
      "5",
      "6",
      "7",
      "8",
      "9",
      "10+",
    ],

    taxClassification: [
      "Individual",
      "Corporation",
      "Partnership",
      "Trust/Estate",
    ],
    taxClassificationes: [
      "Individuo",
      "Corporación",
      "Asociación",
      "Fideicomiso/Patrimonio",
    ],

    foreignPassportCountry: [
      "Afghanistan",
      "Albania",
      "Algeria",
      "Andorra",
      "Angola",
      "Antigua and Barbuda",
      "Argentina",
      "Armenia",
      "Australia",
      "Austria",
      "Azerbaijan",
      "Bahamas",
      "Bahrain",
      "Bangladesh",
      "Barbados",
      "Belarus",
      "Belgium",
      "Belize",
      "Benin",
      "Bhutan",
      "Bolivia",
      "Bosnia and Herzegovina",
      "Botswana",
      "Brazil",
      "Brunei",
      "Bulgaria",
      "Burkina Faso",
      "Burundi",
      "Cambodia",
      "Cameroon",
      "Canada",
      "Cape Verde",
      "Central African Republic",
      "Chad",
      "Chile",
      "China",
      "Colombia",
      "Comoros",
      "Congo",
      "Congo (Dem. Rep.)",
      "Costa Rica",
      "Cote d'Ivoire (Ivory Coast)",
      "Croatia",
      "Cuba",
      "Cyprus",
      "Czech Republic",
      "Denmark",
      "Djibouti",
      "Dominica",
      "Dominican Republic",
      "Ecuador",
      "Egypt",
      "El Salvador",
      "Equatorial Guinea",
      "Eritrea",
      "Estonia",
      "Eswatini",
      "Ethiopia",
      "Fiji",
      "Finland",
      "France",
      "Gabon",
      "Gambia",
      "Georgia",
      "Germany",
      "Ghana",
      "Greece",
      "Grenada",
      "Guatemala",
      "Guinea",
      "Guinea-Bissau",
      "Guyana",
      "Haiti",
      "Honduras",
      "Hong Kong",
      "Hungary",
      "Iceland",
      "India",
      "Indonesia",
      "Iran",
      "Iraq",
      "Ireland",
      "Israel",
      "Italy",
      "Jamaica",
      "Japan",
      "Jordan",
      "Kazakhstan",
      "Kenya",
      "Kiribati",
      "Kosovo",
      "Kuwait",
      "Kyrgyzstan",
      "Laos",
      "Latvia",
      "Lebanon",
      "Lesotho",
      "Liberia",
      "Libya",
      "Liechtenstein",
      "Lithuania",
      "Luxembourg",
      "Macao",
      "Macedonia",
      "Madagascar",
      "Malawi",
      "Malaysia",
      "Maldives",
      "Mali",
      "Malta",
      "Marshall Islands",
      "Mauritania",
      "Mauritius",
      "Mexico",
      "Micronesia",
      "Moldova",
      "Monaco",
      "Mongolia",
      "Montenegro",
      "Morocco",
      "Mozambique",
      "Myanmar [Burma]",
      "Namibia",
      "Nauru",
      "Nepal",
      "Netherlands",
      "New Zealand",
      "Nicaragua",
      "Niger",
      "Nigeria",
      "North Korea",
      "Norway",
      "Oman",
      "Pakistan",
      "Palau",
      "Palestinian Territories",
      "Panama",
      "Papua New Guinea",
      "Paraguay",
      "Peru",
      "Philippines",
      "Poland",
      "Portugal",
      "Qatar",
      "Romania",
      "Russian Federation",
      "Rwanda",
      "Saint Kitts and Nevis",
      "Saint Lucia",
      "Samoa",
      "San Marino",
      "Sao Tome and Principe",
      "Saudi Arabia",
      "Senegal",
      "Serbia",
      "Seychelles",
      "Sierra Leone",
      "Singapore",
      "Slovakia",
      "Slovenia",
      "Solomon Islands",
      "Somalia",
      "South Africa",
      "South Korea",
      "South Sudan",
      "Spain",
      "Sri Lanka",
      "St. Vincent and the Grenadines",
      "Sudan",
      "Suriname",
      "Sweden",
      "Switzerland",
      "Syria",
      "Taiwan",
      "Tajikistan",
      "Tanzania",
      "Thailand",
      "Timor-Leste",
      "Togo",
      "Tonga",
      "Trinidad and Tobago",
      "Tunisia",
      "Turkey",
      "Turkmenistan",
      "Tuvalu",
      "Uganda",
      "Ukraine",
      "United Arab Emirates",
      "United Kingdom",
      "Uruguay",
      "Uzbekistan",
      "Vanuatu",
      "Vatican City",
      "Venezuela",
      "Viet Nam",
      "Yemen",
      "Zambia",
      "Zimbabwe",
    ],
    foreignPassportCountryes: [
      "Afganistán",
      "Albania",
      "Argelia",
      "Andorra",
      "Angola",
      "Antigua y Barbuda",
      "Argentina",
      "Armenia",
      "Australia",
      "Austria",
      "Azerbaiyán",
      "Bahamas",
      "Baréin",
      "Bangladés",
      "Barbados",
      "Bielorrusia",
      "Bélgica",
      "Belice",
      "Benín",
      "Bután",
      "Bolivia",
      "Bosnia y Herzegovina",
      "Botsuana",
      "Brasil",
      "Brunéi",
      "Bulgaria",
      "Burkina Faso",
      "Burundi",
      "Camboya",
      "Camerún",
      "Canadá",
      "Cabo Verde",
      "República Centroafricana",
      "Chad",
      "Chile",
      "China",
      "Colombia",
      "Comoras",
      "Congo",
      "Congo (Rep. Dem.)",
      "Costa Rica",
      "Costa de Marfil",
      "Croacia",
      "Cuba",
      "Chipre",
      "República Checa",
      "Dinamarca",
      "Yibuti",
      "Dominica",
      "República Dominicana",
      "Ecuador",
      "Egipto",
      "El Salvador",
      "Guinea Ecuatorial",
      "Eritrea",
      "Estonia",
      "Esuatini",
      "Etiopía",
      "Fiyi",
      "Finlandia",
      "Francia",
      "Gabón",
      "Gambia",
      "Georgia",
      "Alemania",
      "Ghana",
      "Grecia",
      "Granada",
      "Guatemala",
      "Guinea",
      "Guinea-Bisáu",
      "Guyana",
      "Haití",
      "Honduras",
      "Hong Kong",
      "Hungría",
      "Islandia",
      "India",
      "Indonesia",
      "Irán",
      "Irak",
      "Irlanda",
      "Israel",
      "Italia",
      "Jamaica",
      "Japón",
      "Jordania",
      "Kazajistán",
      "Kenia",
      "Kiribati",
      "Kosovo",
      "Kuwait",
      "Kirguistán",
      "Laos",
      "Letonia",
      "Líbano",
      "Lesoto",
      "Liberia",
      "Libia",
      "Liechtenstein",
      "Lituania",
      "Luxemburgo",
      "Macao",
      "Macedonia",
      "Madagascar",
      "Malaui",
      "Malasia",
      "Maldivas",
      "Malí",
      "Malta",
      "Islas Marshall",
      "Mauritania",
      "Mauricio",
      "México",
      "Micronesia",
      "Moldavia",
      "Mónaco",
      "Mongolia",
      "Montenegro",
      "Marruecos",
      "Mozambique",
      "Myanmar [Birmania]",
      "Namibia",
      "Nauru",
      "Nepal",
      "Países Bajos",
      "Nueva Zelanda",
      "Nicaragua",
      "Níger",
      "Nigeria",
      "Corea del Norte",
      "Noruega",
      "Omán",
      "Pakistán",
      "Palaos",
      "Territorios Palestinos",
      "Panamá",
      "Papúa Nueva Guinea",
      "Paraguay",
      "Perú",
      "Filipinas",
      "Polonia",
      "Portugal",
      "Catar",
      "Rumania",
      "Federación Rusa",
      "Ruanda",
      "San Cristóbal y Nieves",
      "Santa Lucía",
      "Samoa",
      "San Marino",
      "Santo Tomé y Príncipe",
      "Arabia Saudita",
      "Senegal",
      "Serbia",
      "Seychelles",
      "Sierra Leona",
      "Singapur",
      "Eslovaquia",
      "Eslovenia",
      "Islas Salomón",
      "Somalia",
      "Sudáfrica",
      "Corea del Sur",
      "Sudán del Sur",
      "España",
      "Sri Lanka",
      "San Vicente y las Granadinas",
      "Sudán",
      "Surinam",
      "Suecia",
      "Suiza",
      "Siria",
      "Taiwán",
      "Tayikistán",
      "Tanzania",
      "Tailandia",
      "Timor-Leste",
      "Togo",
      "Tonga",
      "Trinidad y Tobago",
      "Túnez",
      "Turquía",
      "Turkmenistán",
      "Tuvalu",
      "Uganda",
      "Ucrania",
      "Emiratos Árabes Unidos",
      "Reino Unido",
      "Uruguay",
      "Uzbekistán",
      "Vanuatu",
      "Ciudad del Vaticano",
      "Venezuela",
      "Vietnam",
      "Yemen",
      "Zambia",
      "Zimbabue",
    ],

    worksWithPets: ["Yes", "No"],
    worksWithPetses: ["Sí", "No"],

    worksWithSmokers: ["Yes", "No"],
    worksWithSmokerses: ["Sí", "No"],

    ableToDrive: ["Yes", "No"],
    ableToDrivees: ["Sí", "No"],

    hasACar: ["Yes", "No"],
    hasACares: ["Sí", "No"],
  },
};

export const isPicklistField = (objectName: string, fieldName: string): boolean => {
  return (
    picklistFieldDataMap[objectName] !== undefined &&
    picklistFieldDataMap[objectName][fieldName] !== undefined
  );
};

export const getPicklistValues = (objectName: string, fieldName: string): string[] => {
  return isPicklistField(objectName, fieldName)
    ? picklistFieldDataMap[objectName][fieldName]
    : [];
};

export default picklistFieldDataMap;
